// ADR-0034 §5 — `core/embed` 영상 임베드 헬퍼 (provider allowlist)
//
// 본 모듈은 editor (URL → embed 노드 정규화) 와 renderer (저장된 embed src 의
// 출력 시 재검증, defense-in-depth) 양쪽에서 공유. 순수 모듈 — `URL` 만 사용해
// 브라우저/서버 모두 안전.
//
// 보안 원칙: 임의 iframe src 를 신뢰하지 않는다. 신규 삽입은 `normalizeEmbed`
// 가 allowlist provider 의 canonical embed URL 로만 변환하고, 출력은
// `isAllowedEmbedSrc` 로 host 를 한 번 더 검증한다 (legacy/import/외부 테넌트가
// 임의 src 를 DB 에 남겨도 렌더 단계에서 차단).

export type EmbedProvider = "youtube" | "vimeo";

export interface NormalizedEmbed {
  provider: EmbedProvider;
  /** allowlist host 의 canonical iframe src (가능 시 privacy-enhanced). */
  embedSrc: string;
  /** 사용자가 입력한 원본 URL — fallback 링크 / round-trip 보존용. */
  originalUrl: string;
}

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "youtu.be",
  "www.youtu.be",
]);

const VIMEO_HOSTS = new Set(["vimeo.com", "www.vimeo.com", "player.vimeo.com"]);

/** 출력(iframe) 시 허용되는 embed src host — render 단계 defense-in-depth. */
const ALLOWED_EMBED_HOSTS = new Set([
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
  "www.youtube.com",
  "youtube.com",
  "player.vimeo.com",
]);

const YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;
const VIMEO_ID = /^\d+$/;

/**
 * 사용자 입력 URL → allowlist provider 의 canonical embed. 지원하지 않는
 * provider / 파싱 실패 시 `null`.
 */
export function normalizeEmbed(input: unknown): NormalizedEmbed | null {
  if (typeof input !== "string") return null;
  const raw = input.trim();
  if (raw.length === 0) return null;

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;

  const host = url.hostname.toLowerCase();

  if (YOUTUBE_HOSTS.has(host)) {
    const id = extractYouTubeId(url, host);
    if (id) {
      return {
        provider: "youtube",
        embedSrc: `https://www.youtube-nocookie.com/embed/${id}`,
        originalUrl: raw,
      };
    }
  }

  if (VIMEO_HOSTS.has(host)) {
    const id = extractVimeoId(url, host);
    if (id) {
      return {
        provider: "vimeo",
        embedSrc: `https://player.vimeo.com/video/${id}`,
        originalUrl: raw,
      };
    }
  }

  return null;
}

/**
 * 이미 저장된 embed src 가 allowlist host(https) 인지 검증 — 렌더러가 iframe
 * 출력 전 호출. 임의 host / 비 https 는 거부.
 */
export function isAllowedEmbedSrc(value: unknown): value is string {
  if (typeof value !== "string") return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  if (!ALLOWED_EMBED_HOSTS.has(host)) return false;
  // host 뿐 아니라 canonical embed path 형태까지 검증 — 허용 host 의 임의 경로
  // (예: 스크립트·홈) 가 iframe 으로 로드되는 것을 차단.
  if (host === "player.vimeo.com") {
    return /^\/video\/\d+$/.test(url.pathname);
  }
  // youtube / youtube-nocookie → /embed/<11-char id>
  return new RegExp("^/embed/[A-Za-z0-9_-]{11}$").test(url.pathname);
}

/** 지도 embed 허용 host — keyless embed 만 (API 키 의존 provider 금지). */
const GOOGLE_MAP_HOSTS = new Set(["www.google.com", "google.com", "maps.google.com"]);
const OSM_HOSTS = new Set(["www.openstreetmap.org", "openstreetmap.org"]);

/**
 * 지도 embed src allowlist — 렌더러가 contact 지도 iframe 출력 전 호출
 * (W8-7, `isAllowedEmbedSrc` 의 영상 allowlist 와 별도).
 *
 * 별도 함수인 이유: 지도는 "외부 API 키가 필요 없는 keyless embed 만"이라는
 * 추가 제약이 있다. 허용 형태:
 *   - Google Maps 공유 embed:  https://www.google.com/maps/embed?pb=…
 *   - Google Maps keyless 쿼리 embed: https://maps.google.com/maps?q=…&output=embed
 *   - OpenStreetMap export embed: https://www.openstreetmap.org/export/embed.html?…
 * `/maps/embed/v1/*`(Embed API — 키 필요) 와 그 밖의 host/경로는 거부 —
 * 거부 시 렌더러는 기존 facade 이미지/길찾기 링크 폴백을 유지한다(fail-soft).
 */
export function isAllowedMapEmbedSrc(value: unknown): value is string {
  if (typeof value !== "string") return false;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return false;
  }
  if (url.protocol !== "https:") return false;
  const host = url.hostname.toLowerCase();
  if (GOOGLE_MAP_HOSTS.has(host)) {
    if (url.pathname === "/maps/embed") return true;
    if (url.pathname === "/maps" || url.pathname === "/maps/") {
      return url.searchParams.get("output") === "embed";
    }
    return false;
  }
  if (OSM_HOSTS.has(host)) {
    return url.pathname === "/export/embed.html";
  }
  return false;
}

function extractYouTubeId(url: URL, host: string): string | null {
  if (host === "youtu.be" || host === "www.youtu.be") {
    const id = url.pathname.split("/").filter(Boolean)[0] ?? "";
    return YOUTUBE_ID.test(id) ? id : null;
  }
  const v = url.searchParams.get("v");
  if (v && YOUTUBE_ID.test(v)) return v;
  const parts = url.pathname.split("/").filter(Boolean);
  if (
    parts.length >= 2 &&
    (parts[0] === "embed" || parts[0] === "shorts" || parts[0] === "v")
  ) {
    return YOUTUBE_ID.test(parts[1]) ? parts[1] : null;
  }
  return null;
}

function extractVimeoId(url: URL, host: string): string | null {
  const parts = url.pathname.split("/").filter(Boolean);
  let candidate: string | undefined;
  if (host === "player.vimeo.com") {
    const vi = parts.indexOf("video");
    candidate = vi >= 0 ? parts[vi + 1] : parts[parts.length - 1];
  } else {
    candidate = parts[0];
  }
  return candidate && VIMEO_ID.test(candidate) ? candidate : null;
}
